import React, { useEffect, useMemo, useState } from "react";
import swal from "sweetalert";
import AssessmentRow from "./AssessmentRow";
import moment from "moment";
import { NepaliDatePicker } from "nepali-calander";
import { compareFullDateTime } from "../../../../utils";
import Button from "../../../shared/Button";

const SubjectConfigurationForm = ({
  yearLevelId,
  classroomId,
  subjects,
  setAssessmentSubjectConfigurations,
  assessmentSubjectConfigurations,
  setAssessmentGeneralConfigurations,
  assessmentGeneralConfigurations,
  recordingDateCheckbox,
  recordingDate,
  defaultStartTime,
  defaultHour,
  defaultMinutes,
}) => {
  const [calendarMode, setCalendarMode] = useState(
    localStorage.getItem("calendar-mode"),
  );
  const [selectAll, setSelectAll] = useState(false);

  const selectedGeneralConfiguration = useMemo(() => {
    return (
      assessmentGeneralConfigurations?.find(
        (a) => a?.yearLevelId === yearLevelId && a?.classroomId === classroomId,
      ) ?? []
    );
  }, [assessmentGeneralConfigurations]);

  const handleChange = (field, value, yearLevelId, classroomId, subjectId) => {
    setAssessmentSubjectConfigurations((prev) => {
      return prev?.map((row) => {
        if (
          row.yearLevelId !== yearLevelId ||
          row.classroomId !== classroomId ||
          row.subjectId !== subjectId
        ) {
          return row;
        }

        const updatedRow = { ...row, [field]: value };

        // auto set theory recording deadline
        if (field === "recordingTheoryDate" && recordingDateCheckbox) {
          updatedRow.recordingTheoryDeadlineDate = moment(value)
            .add(recordingDate.date, recordingDate.type)
            .format("YYYY-MM-DD");
        }

        // auto set practical recording deadline
        if (field === "recordingPracticalDate" && recordingDateCheckbox) {
          updatedRow.recordingPracticalDeadlineDate = moment(value)
            .add(recordingDate.date, recordingDate.type)
            .format("YYYY-MM-DD");
        }

        return updatedRow;
      });
    });
  };

  const handleGeneralConfigurationChange = (field, value) => {
    setAssessmentGeneralConfigurations((prev) => {
      const index = prev?.findIndex(
        (row) =>
          row.yearLevelId === yearLevelId && row.classroomId === classroomId,
      );

      const rows = [...prev];

      const current =
        index === -1 ? { yearLevelId, classroomId } : { ...rows[index] };

      current[field] = value;

      if (field === "recordingDate") {
        current.recordingDeadlineDate = moment(value)
          .add(recordingDate.date, recordingDate.type)
          .format("YYYY-MM-DD");
      }

      // update rows
      if (index === -1) {
        rows.push(current);
      } else {
        rows[index] = current;
      }

      return rows;
    });
  };

  const handleSelectAll = (e) => {
    const { checked } = e.target;

    setSelectAll(checked);

    setAssessmentSubjectConfigurations((prev) => {
      const updated = prev?.map((row) => ({
        ...row,
        checked: checked || row?.isRecorded,
      }));
      return updated;
    });
  };

  const handleClearGeneralConfig = () => {
    setAssessmentGeneralConfigurations((prev) => {
      const updated = prev?.filter(
        (item) =>
          !(
            item?.yearLevelId === yearLevelId &&
            item?.classroomId === classroomId
          ),
      );
      return updated;
    });
  };

  useEffect(() => {
    if (assessmentSubjectConfigurations.length === 0) {
      setSelectAll(false);
      return;
    }

    const isAllChecked = assessmentSubjectConfigurations.every(
      (row) => row.checked,
    );

    setSelectAll(isAllChecked);
  }, [assessmentSubjectConfigurations]);

  return (
    <div
      style={{
        border: "none",
        padding: "34px 10px 10px 10px",
        position: "relative",
      }}
      className="arr-table-container"
    >
      <table className="arr-table">
        <thead>
          <tr>
            <th>
              <input
                checked={selectAll}
                onChange={handleSelectAll}
                className="checkbox"
                type="checkbox"
              />
            </th>
            <th>Subjects</th>
            <th>Type</th>
            <th>
              <span className="required">Assessment Date</span>
            </th>
            <th>
              <span className="required">Start Time</span>
            </th>
            <th>
              <span className="required">Duration</span>
            </th>
            <th>Recording Date</th>
            <th>Recording Deadline</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {subjects.length > 0 ? (
            subjects?.map((item) => {
              return (
                <React.Fragment key={item.subjectId}>
                  <AssessmentRow
                    hasTheory={item?.hasTheory}
                    hasPractical={item?.hasPractical}
                    classroomId={classroomId}
                    yearLevelId={yearLevelId}
                    subjectId={item?.subjectId}
                    subjectName={item?.subjectName}
                    handleChange={handleChange}
                    assessmentSubjectConfigurations={
                      assessmentSubjectConfigurations
                    }
                    setAssessmentSubjectConfigurations={
                      setAssessmentSubjectConfigurations
                    }
                    defaultStartTime={defaultStartTime}
                    defaultHour={defaultHour}
                    defaultMinutes={defaultMinutes}
                    setSelectAll={setSelectAll}
                  />
                </React.Fragment>
              );
            })
          ) : (
            <tr>
              <td style={{ textAlign: "center" }} colSpan={8}>
                No Data Found
              </td>
            </tr>
          )}
        </tbody>
      </table>

      <div
        style={{
          marginTop: "24px",
          position: "sticky",
          bottom: 0,
          left: 0,
          right: 0,
        }}
        className="arr-form"
      >
        <p style={{ margin: "auto 0", whiteSpace: "nowrap", fontWeight: 500 }}>
          General Assessment
        </p>

        <div className="arr-form noShadow arr-general-assessment">
          <div
            style={{ display: "flex", alignItems: "end", marginBottom: "8px" }}
          >
            <input
              type="checkbox"
              className="checkbox"
              name="checked"
              checked={selectedGeneralConfiguration?.checked || false}
              disabled={selectedGeneralConfiguration?.isRecorded}
              onChange={(e) => {
                const checked = e.target.checked;
                handleGeneralConfigurationChange("checked", checked);
              }}
            />
          </div>
          <div className="form-input">
            <label className="required" htmlFor="assessmentFromDate">
              From Date
            </label>
            <NepaliDatePicker
              styles={{ width: "100% !important" }}
              type="input"
              showToggle
              value={selectedGeneralConfiguration?.assessmentFromDate || ""}
              showNationalHoliday={false}
              calendarMode={calendarMode}
              onCalendarModeChange={(mode) => setCalendarMode(mode)}
              onDateSelect={(date) => {
                handleGeneralConfigurationChange(
                  "assessmentFromDate",
                  date?.ad?.date,
                );
              }}
            />
          </div>
          <div className="form-input">
            <label className="required" htmlFor="assessmentToDate">
              To Date
            </label>
            <NepaliDatePicker
              styles={{ width: "100% !important" }}
              type="input"
              showToggle
              value={selectedGeneralConfiguration?.assessmentToDate || ""}
              showNationalHoliday={false}
              calendarMode={calendarMode}
              onCalendarModeChange={(mode) => setCalendarMode(mode)}
              onDateSelect={(date) => {
                handleGeneralConfigurationChange(
                  "assessmentToDate",
                  date?.ad?.date,
                );
              }}
              disabled={!selectedGeneralConfiguration?.assessmentFromDate || ""}
              minDate={moment(
                selectedGeneralConfiguration?.assessmentFromDate,
              ).format("YYYY-MM-DD")}
            />
          </div>
          <div className="form-input">
            <label htmlFor="recordingDate">Recording Date</label>
            <NepaliDatePicker
              styles={{ width: "100% !important" }}
              type="input"
              value={selectedGeneralConfiguration?.recordingDate || ""}
              showToggle
              showNationalHoliday={false}
              calendarMode={calendarMode}
              onCalendarModeChange={(mode) => setCalendarMode(mode)}
              onDateSelect={(date) => {
                handleGeneralConfigurationChange(
                  "recordingDate",
                  date?.ad?.date,
                );
              }}
              disabled={!selectedGeneralConfiguration?.assessmentToDate}
              minDate={moment(
                selectedGeneralConfiguration?.assessmentToDate,
              ).format("YYYY-MM-DD")}
            />
          </div>
          <div className="form-input">
            <label htmlFor="recordingDeadlineDate">
              Recording Deadline Date
            </label>
            <NepaliDatePicker
              styles={{ width: "100% !important" }}
              type="input"
              showToggle
              value={selectedGeneralConfiguration?.recordingDeadlineDate || ""}
              showNationalHoliday={false}
              calendarMode={calendarMode}
              onCalendarModeChange={(mode) => setCalendarMode(mode)}
              onDateSelect={(date) => {
                handleGeneralConfigurationChange(
                  "recordingDeadlineDate",
                  date?.ad?.date,
                );
              }}
              disabled={!selectedGeneralConfiguration?.recordingDate}
              minDate={moment(
                selectedGeneralConfiguration?.recordingDate,
              ).format("YYYY-MM-DD")}
            />
          </div>
          <div
            style={{ display: "flex", alignItems: "end", marginBottom: "8px" }}
          >
            <Button
              onClick={handleClearGeneralConfig}
              size="sm"
              variant="danger"
              disabled={selectedGeneralConfiguration?.isRecorded}
            >
              Clear
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubjectConfigurationForm;
